import { ref } from "vue";
import { getEnvVar } from "@/utils/env";
import { defineStore } from "pinia";
import { DateTime, Interval } from "luxon";
import { getTravelInterval, LANG, loadPromoBadges } from "vacv-common";
import {
  PromoId,
  HotelId,
  Promos,
  Promo,
  LocalePromo,
  PromoIdAndHotel,
} from "@/../types/promos";

const promosMap = new Map<PromoId, Promo>();
const hotelPromosMap = new Map<HotelId, PromoIdAndHotel[]>();
export const isLoaded = ref(false);

export const usePromosStore = defineStore("promos", async () => {
  if (!isLoaded.value) {
    const promoBadgesUrl =
      getEnvVar("VITE_CDN_DOMAIN") + getEnvVar("VITE_PROMO_BADGE_PATH");
    const retryTimes = getEnvVar("VITE_JSON_RETRY_TIMES");
    const promos: Promos | undefined = await loadPromoBadges(
      promoBadgesUrl,
      retryTimes
    );
    // for efficient lookups populate the promosMap and hotelPromosMap
    if (promos?.Promos?.length) {
      for (const promo of promos.Promos) {
        promosMap.set(promo.promo_id, promo);

        for (const hotel of promo.Hotel_List) {
          // store the hotel along with the promo id so we can look it up quickly later on
          const promoAndHotels: PromoIdAndHotel[] =
            hotelPromosMap.get(hotel.Hotel_Code) || [];
          promoAndHotels.push({
            promo_id: promo.promo_id,
            ...hotel,
          } as PromoIdAndHotel);
          hotelPromosMap.set(hotel.Hotel_Code, promoAndHotels);
        }
      }
    }
    isLoaded.value = true;
  }

  return {
    isLoaded,
  };
});

export const resetPromos = () => {
  promosMap.clear();
  hotelPromosMap.clear();
  isLoaded.value = false;
};

// FYI the date format here differs from the promosStore in packages-spa
const CHECKIN_DATE_FORMAT = "yyyy-MM-dd";

export function getPromoById(
  hotelId: string,
  lang: LANG,
  packageCheckIn: string,
  packageCheckOut: string
): LocalePromo | undefined {
  if (!isLoaded.value) {
    return;
  }

  const hotelPromos = hotelPromosMap.get(hotelId);
  if (!hotelPromos) {
    return;
  }

  const validPromos = hotelPromos.filter((hotelPromo) => {
    return isValidPromo(hotelPromo, packageCheckIn, packageCheckOut);
  });

  if (validPromos.length) {
    // use the last valid promo
    const promoIdAndHotel = validPromos[validPromos.length - 1];
    const promoId = promoIdAndHotel.promo_id;
    const promo: Promo | undefined = promosMap.get(promoId);
    if (promo) {
      return getLocalePromo(promo, promoIdAndHotel.substitution, lang);
    }
  }
}

function isValidPromo(
  hotelPromo: PromoIdAndHotel,
  packageCheckIn: string,
  packageCheckOut: string
) {
  const promoId = hotelPromo.promo_id;

  const promo: Promo | undefined = promosMap.get(promoId);
  if (!promo?.["Travel_Start_Date"] || !promo?.["Travel_End_Date"]) {
    // this promo does not have Travel_Start_Date or Travel_End_Date so it is invalid
    return false;
  }

  const packageCheckInDate = DateTime.fromFormat(
    packageCheckIn,
    CHECKIN_DATE_FORMAT
  );
  const packageCheckOutDate = DateTime.fromFormat(
    packageCheckOut,
    CHECKIN_DATE_FORMAT
  );

  const travelInterval = getTravelInterval(
    promo["Travel_Start_Date"],
    promo["Travel_End_Date"]
  );

  // ACVW-10474: package check in/out dates must be within the promo travel start/end dates, inclusively
  if (
    travelInterval.contains(packageCheckInDate) &&
    (travelInterval.contains(packageCheckOutDate) ||
      promo["Travel_End_Date"] === packageCheckOutDate.toFormat("yyyy/MM/dd"))
  ) {
    const hotelStartDate = DateTime.fromFormat(
      hotelPromo.Start_Date,
      "yyyy/MM/dd"
    );
    const hotelEndDate = DateTime.fromFormat(hotelPromo.End_Date, "yyyy/MM/dd");

    const promoInterval = Interval.fromDateTimes(hotelStartDate, hotelEndDate);
    const todayDate = DateTime.now();

    if (promoInterval.contains(todayDate)) {
      return true;
    }
  }

  return false;
}

const getLocalePromo = (
  promo: Promo,
  substitution: string,
  lang: LANG
): LocalePromo => {
  const token = "%%substitution%%";
  const promoLocale = promo[`locale_${lang}`];
  const Badge = promoLocale.Badge.split(token).join(substitution);
  const Terms = promoLocale.Terms.split(token).join(substitution);
  return {
    Badge,
    Terms,
  };
};
